import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { BellOutlined } from '@ant-design/icons';
import { Badge, Button, Popover } from 'antd';
import type { ArgsProps } from 'antd/es/notification/interface';

import {
    NotificationList,
    NotificationModal,
    useNotificationDescription,
    useNotificationTitle,
} from '@/features/notification-display';

import {
    type Notification,
    NotificationType,
    useGetNotificationsQuery,
    useMarkAsDeliveredMutation,
    useMarkAsReadMutation,
    useUnreadNotificationCount,
} from '@/entities/notification';

import { useAntNotification } from '@/shared/lib/hooks';

import cls from './notification-button.module.css';

export const NotificationButton = memo(() => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [localUnreadCount, setLocalUnreadCount] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] =
        useState<Notification | null>(null);

    const { refetch, data } = useGetNotificationsQuery({
        offset: 0,
    });

    const unreadCount = useUnreadNotificationCount();
    const [markAsRead] = useMarkAsReadMutation();
    const [markAsDelivered] = useMarkAsDeliveredMutation();

    // Get notification functions from context
    const { showInfo, showWarning, showSuccess } = useAntNotification();

    // Handle opening the notification modal
    const handleOpenModal = useCallback(
        (notification: Notification) => {
            setSelectedNotification(notification);
            setIsModalOpen(true);
            markAsRead(notification.id);
        },
        [markAsRead],
    );

    // Handle closing the notification modal
    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedNotification(null);
    }, []);

    // Get the newest undelivered notification
    const newestUndeliveredNotification = useMemo(() => {
        const undelivered =
            data?.results?.filter((notification) => !notification.delivered) ||
            [];

        if (undelivered.length === 0) return null;

        return (
            [...undelivered].sort(
                (a, b) =>
                    new Date(b.timestamp).getTime() -
                    new Date(a.timestamp).getTime(),
            )[0] || null
        );
    }, [data?.results]);

    // Get translations for the newest notification
    const newestNotificationTitle = useNotificationTitle(
        newestUndeliveredNotification,
    );
    const newestNotificationDescription = useNotificationDescription(
        newestUndeliveredNotification,
    );

    const handlePopoverOpenChange = (open: boolean) => {
        setIsPopoverOpen(open);
    };

    useEffect(() => {
        const intervalId = setInterval(() => {
            refetch();
        }, 5000);

        return () => clearInterval(intervalId);
    }, [refetch]);

    // Show notification prompt when new undelivered notifications arrive
    useEffect(() => {
        // Initialize a state to keep track of previous unread count
        setLocalUnreadCount(unreadCount);

        // Check if the unread count has increased since the last check
        // and there is an undelivered notification to show
        if (unreadCount > localUnreadCount && newestUndeliveredNotification) {
            // Show notification prompt with click handler based on the notification type
            const notificationProps: ArgsProps = {
                message: newestNotificationTitle,
                description: newestNotificationDescription,
                placement: 'topRight',
                onClick: () =>
                    newestUndeliveredNotification &&
                    handleOpenModal(newestUndeliveredNotification),
                style: { cursor: 'pointer' },
            };

            switch (newestUndeliveredNotification.tipo) {
                case NotificationType.PATTERN_ALERT:
                    showInfo?.(notificationProps);

                    break;
                case NotificationType.SUBSCRIPTION_WARNING:
                    showWarning?.(notificationProps);

                    break;

                default:
                    showInfo?.(notificationProps);
            }

            // Mark notification as delivered immediately after showing popup
            markAsDelivered(newestUndeliveredNotification.id);
        }

        setLocalUnreadCount(unreadCount);
    }, [
        unreadCount,
        newestUndeliveredNotification,
        newestNotificationTitle,
        newestNotificationDescription,
        showInfo,
        showWarning,
        showSuccess,
        handleOpenModal,
        localUnreadCount,
        markAsDelivered,
    ]);

    return (
        <>
            <Popover
                arrow={false}
                content={
                    <NotificationList
                        onClose={() => handlePopoverOpenChange(false)}
                    />
                }
                onOpenChange={handlePopoverOpenChange}
                open={isPopoverOpen}
                placement="bottomRight"
                rootClassName={cls.root}
                trigger="click"
            >
                <Badge
                    count={unreadCount}
                    data-tour="dashboard-notification-button"
                    size="small"
                >
                    <Button
                        className={cls.trigger}
                        icon={<BellOutlined />}
                        type="text"
                    />
                </Badge>
            </Popover>

            <NotificationModal
                isOpen={isModalOpen}
                notification={selectedNotification}
                onClose={handleCloseModal}
            />
        </>
    );
});

NotificationButton.displayName = 'NotificationButton';
